<template>
    <div class="product-card__container" :data-product-id="product.id" @mouseenter="startHoverTimer" @mouseleave="clearHoverTimer">
        <div
            class="product-card"
            :class="`js-product-stats js-product-stats-${product.id} ${isCarousel ? 'product-card--carousel' : ''} ${
                visualMode === 'horizontal' ? 'product-card--horizontal' : ''
            }`"
            :data-id="product.id"
        >
            <div v-if="(visualMode === 'vertical' || product.isSponsored) && !minimalizedProductVisual" class="product-card__sponsored">
                {{ product.isSponsored ? $t('product.sponsored') : '' }}
            </div>
            <div class="product-card__img-container">
                <a
                    class="product-card__img-container__link"
                    :href="product.url"
                    tabindex="-1"
                    @click.prevent="$emit('productRedirectedToUrl', $event)"
                >
                    <ElementsImagePreview
                        :image-url="product.imageCdn"
                        :image-alt="product.name"
                        :lazy-image="lazyImage"
                        :out-of-stock-greyed-out="product.attributes?.outOfStockGreyedOut || !product.isAvailable"
                        :image-by-size="product.imageBySize || null"
                        class="product-card__img-container__preview"
                    />

                    <div class="product-card__visual-tag-images">
                        <div v-for="(badge, index) in visualTagImages" :key="index" class="product-card__visual-tag-image">
                            <img v-if="badge.image" :src="badge.image" :alt="badge.name" />
                        </div>
                    </div>

                    <div class="product-card__visual-tags">
                        <div
                            v-if="product.isPremiumExclusive"
                            class="product-card__visual-tag product-card__visual-tag--sm"
                            :style="{ 'background-color': '#e6007e', color: '#fff' }"
                        >
                            {{ $t('product.productIsPremiumExclusive') }}
                        </div>

                        <div
                            v-for="(badge, index) in visualTags"
                            :key="index"
                            class="product-card__visual-tag"
                            :class="`product-card__visual-tag--${badge.size}`"
                            :style="{ 'background-color': badge.backgroundColor, color: badge.textColor }"
                        >
                            {{ badge.name }}
                        </div>

                        <div
                            v-if="product.priceFrom"
                            class="product-card__visual-tag"
                            :class="`product-card__visual-tag--xl`"
                            :style="{ 'background-color': '#146154', color: 'rgb(255, 255, 255)' }"
                        >
                            <b v-html="`${$t('productDetail.fromPrice', { price: $formatCurrency(product.priceFrom) })}`"></b>
                        </div>

                        <ElementsBadgeSquare
                            v-if="product.pricePerPiece"
                            class="product-card__visual-tag"
                            :price-per-piece="product.pricePerPiece"
                        />
                    </div>
                </a>

                <ObjectsProductFavoriteButton v-show="authUser" :product="product" class="product-card__favorite-button" :small="true" />

                <div v-if="customButtonTitle" class="product-card__add-basket">
                    <TheButton :title="customButtonTitle" @click="$emit('customButtonClicked', product.id)" />
                </div>
                <div v-else-if="showAddToBasket" class="product-card__add-basket">
                    <ElementsProductCardAddToBasket
                        v-if="product.isAvailable && !hasVariants"
                        ref="addToBasketButton"
                        :add-basket-align="``"
                        :product="product"
                        :product-id="product.id"
                        :product-price="product.price"
                        :is-preorder="product.isPreorder"
                        :max-amount-per-order="product.attributes.maxAmountPerOrder"
                        :has-substitution-modal="product.substitutions?.isModal || false"
                        :list-position="listPosition"
                        :list-type="listType"
                        :item-list-name="itemListName"
                        :carousel-name="carouselName"
                        :list-title="listTitle"
                        :show-pre-basket="showPreBasket"
                        show-actual-state-mode="small"
                        :addon-sale-behavior="addonSaleBehavior"
                        @product-added-to-basket="(productUrl: string) => $emit('productAddedToBasket', productUrl)"
                    />
                    <ObjectsProductVariantsLink
                        v-else-if="hasVariants"
                        :product="product"
                        @redirect-to-url="$emit('productRedirectedToUrl', $event)"
                    />
                </div>
            </div>

            <div class="product-card__main">
                <a :href="product.url" style="display: block; width: 100%" @click.prevent="$emit('productRedirectedToUrl', $event)">
                    <ElementsProductCardPrice
                        :has-super-price="product.hasSuperPrice"
                        :has-openers-price="product.hasOpenersPrice"
                        :has-competitor-super-price="product.hasCompetitorSuperPrice"
                        :price="product.price"
                        :price-before-discount="product.priceBeforeDiscount"
                        :addon-sale-behavior="addonSaleBehavior"
                        :addon-sale-price="product.attributes.addonSalePrice"
                        :price-from="product.priceFrom"
                    />

                    <ObjectsProductGiftActions
                        v-if="!minimalizedProductVisual"
                        class="product-card__gift-actions"
                        :product="product"
                        :include-product-code-sale="true"
                    />
                </a>

                <nuxt-link v-if="product.brandName" :to="product.brandUrl || '/brand'" class="product-card__title__brand">{{
                    product.brandName
                }}</nuxt-link>

                <a :href="product.url" style="display: block; width: 100%" @click.prevent="$emit('productRedirectedToUrl', $event)">
                    <h3 class="product-card__title">
                        <span class="product-card__title__name" v-html="formattingTitle(productNameWithoutBrand)"></span>
                    </h3>

                    <div class="mb-2">
                        <ElementsProductCardNutraRating
                            v-if="product.nutraRatingScoreLetter && (showNutraRating || product.nutraRatingIsVisible)"
                            :nutra-rating-letter="product.nutraRatingScoreLetter"
                            :simple="true"
                        />
                    </div>

                    <p
                        v-if="showDescription && !minimalizedProductVisual"
                        class="product-card__description"
                        v-html="product.description"
                    ></p>

                    <div v-if="comparablePrice" class="product-card__params">
                        <!--                    <div v-if="product.packSizeHumanReadableValue">{{ product.packSizeHumanReadableValue }}</div>-->
                        <div v-if="comparablePrice" v-html="comparablePrice"></div>
                        <!--showComparablePrice-->
                    </div>
                </a>

                <div v-if="showAvailability && !hasVariants" class="product-card__availability">
                    <img
                        v-if="product.amountInStock && product.isAvailable && !minimalizedProductVisual"
                        src="/nuxt-assets/icons/rocket-green2.svg"
                        alt="Rocket"
                    />
                    <img v-else-if="!minimalizedProductVisual" src="/nuxt-assets/icons/auticko.svg" alt="Car" />
                    <ObjectsProductAvailability :product="product" :has-variants="hasVariants" />
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { PropType } from 'vue'
import { useI18n } from 'vue-i18n'
import Product from '~/models/Product/Product'
import { useBasketStore } from '~/stores/basket'
import ProductVisualTag from '~/models/Product/ProductVisualTag'
import TheButton from '~/components/ui/TheButton.vue'
import useHoverPreload from '~/composables/useHoverPreload'
import useProductDetailAsyncData from '~/composables/productDetail/useProductDetailAsyncData'

const props = defineProps({
    product: {
        type: Object as PropType<Product>,
        required: true,
    },
    lazyImage: {
        type: Boolean,
        default: true,
    },
    showComparablePrice: {
        type: Boolean,
        default: true,
    },
    showAvailability: {
        type: Boolean,
        default: true,
    },
    isCarousel: {
        type: Boolean,
        default: false,
    },
    listPosition: {
        type: Number,
        default: null,
    },
    listTitle: {
        type: String,
        default: '',
    },
    listType: {
        type: String,
        default: '',
    },
    itemListName: {
        type: String,
        default: '',
    },
    carouselName: {
        type: String,
        default: '',
    },
    showPreBasket: {
        type: Boolean,
        default: true,
    },
    addonSaleBehavior: {
        type: Boolean,
        default: false,
    },
    showAddToBasket: {
        type: Boolean,
        default: true,
    },
    visualMode: {
        type: String,
        default: 'vertical',
    },
    showDescription: {
        type: Boolean,
        default: false,
    },
    showNutraRating: {
        type: Boolean,
        default: false,
    },
    customButtonTitle: {
        type: String,
        default: null,
        required: false,
    },
    minimalizedProductVisual: {
        type: Boolean,
        default: false,
    },
})

defineEmits(['productRedirectedToUrl', 'productAddedToBasket', 'customButtonClicked'])

const authUser = computed(() => {
    const basketStore = useBasketStore()
    return basketStore.authUser
})

const formattingTitle = (title: string): string => {
    return title?.replace(new RegExp(` (?=${useI18n().t('stripSpaceSymbol')})`, 'g'), '&nbsp;')
}

const productNameWithoutBrand = computed(() => {
    return props.product.name.replace(new RegExp(`^${props.product.brandName}`), '').trim()
})

const hasVariants = computed(() => {
    // eslint-disable-next-line no-prototype-builtins
    return props.product.hasOwnProperty('variantsCount') && props.product.variantsCount > 0
})

const visualTags = computed(() => {
    return (
        props.product.visualTags.merged
            .filter((visualTag: ProductVisualTag) => visualTag.showInList && !visualTag.showAsImageOnly)
            .slice(0, 1) || []
    )
})

const visualTagImages = computed(() => {
    return (
        props.product.visualTags.merged
            .filter((visualTag: ProductVisualTag) => visualTag.showInList && visualTag.showAsImageOnly)
            .slice(0, 1) || []
    )
})

const comparablePrice = computed(() => {
    return props.product.attributes.comparablePrice
})

const productPreloaded = ref(false)
const nuxtApp = useNuxtApp()
const preloadProductData = () => {
    if (productPreloaded.value) {
        return
    }

    productPreloaded.value = true

    // @ts-ignore
    nuxtApp.$prefetchUrl({ fullPath: props.product.url }, {})
    useProductDetailAsyncData(props.product.id)
}

const { startHoverTimer, clearHoverTimer } = useHoverPreload(preloadProductData, 2000)
</script>

<style lang="scss" scoped>
.product-card {
    margin: 0 auto;
    width: 100%;
    text-align: left;
    display: grid;
    grid-template-areas:
        'sponsored'
        'imgContainer'
        'main';

    &__container {
        container-type: inline-size;
        width: 100%;

        @include screen-lg {
        }
    }

    &--horizontal {
        grid-template-columns: 35% 1fr;
        column-gap: 4%;
        grid-template-areas:
            'sponsored sponsored'
            'imgContainer main';
    }

    &__main {
        grid-area: main;
        margin-top: 10px;
        overflow: hidden;
    }

    &__sponsored {
        grid-area: sponsored;
        color: $color-grey-extra-dark;
        font-size: 13px;
        height: 25px;
    }

    @include screen-lg {
        width: 100%;
    }

    &--carousel {
        width: 95%;
        padding: 0;
    }

    &__img-container {
        grid-area: imgContainer;
        position: relative;
        display: block;
        aspect-ratio: 1 / 1;

        &__preview {
            position: absolute;
            left: 20px;
            top: 20px;
            bottom: 20px;
            right: 20px;
            width: calc(100% - 40px);
        }

        &__link {
            position: absolute;
            left: 0;
            right: 0;
            top: 0;
            bottom: 0;
        }

        &::after {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background-color: rgba(#596a7e, 0.05);
            pointer-events: none;
            border-radius: 10px;
        }

        &.small {
            height: 50px;

            @include screen-sm() {
                height: 100px;
            }
        }
    }

    &__favorite-button {
        @include screen-sm() {
            display: none;
        }
    }

    &__title {
        margin: 0 0 5px;
        //height: 44px;
        font-size: 15px;
        line-height: 20px;
        color: $color-secondary-dark-green-1;
        display: flex;
        flex-direction: column;
        gap: 5px;

        @container (min-width: 270px) {
            font-size: 18px;
            line-height: 28px;
        }

        &__brand {
            font-size: 14px;
            font-weight: 600;
            text-decoration: underline;
            color: $color-secondary-dark-green-2;
            line-height: 20px;
            margin-bottom: 5px;
            display: block;
        }

        &__name {
            font-weight: 500;
            -webkit-line-clamp: 2;
            line-clamp: 2;
            -webkit-box-orient: vertical;
            text-overflow: ellipsis;
            overflow: hidden;
            display: -webkit-box;
            overflow-wrap: anywhere;
        }
    }

    &__description {
        color: $color-grey-extra-dark;
        margin: 0 0 10px;
        font-size: 13px;
        line-height: 16px;
        -webkit-line-clamp: 2;
        line-clamp: 2;
        -webkit-box-orient: vertical;
        text-overflow: ellipsis;
        overflow: hidden;
        display: -webkit-box;
    }

    &__params {
        color: $color-grey-500;
        margin-bottom: 15px;
        display: flex;
        gap: 10px;
        font-size: 13px;
        //white-space: nowrap;

        @container (min-width: 280px) {
            font-size: 15px;
        }

        &__price_per_piece {
            -webkit-line-clamp: 1;
            line-clamp: 1;
            -webkit-box-orient: vertical;
            text-overflow: ellipsis;
            overflow: hidden;
        }
    }

    &__visual-tags {
        position: absolute;
        top: 7px;
        right: 6px;
        left: 0;
        //display: block;
        display: flex;
        flex-wrap: wrap;
        flex-direction: column;
        align-items: end;
        text-align: right;

        @container (min-width: 280px) {
            top: 10px;
            right: 10px;
        }
    }

    &__visual-tag {
        display: inline-block;
        font-size: 11px;
        line-height: 14px;
        border-radius: 25px;
        padding: 3px 10px;
        margin-bottom: 3px;
        font-weight: 500;
        max-width: 91%;

        @container (min-width: 280px) {
            font-size: 12px;
        }
    }

    &__visual-tag-images {
        position: absolute;
        top: 10px;
        left: 10px;

        @container (min-width: 280px) {
            top: 10px;
            right: 10px;
        }
    }

    &__visual-tag-image {
        width: 50px;
    }

    &__gift-actions {
        background: $color-tercial-light-green-3;
        padding: 5px 7px;
        border-radius: 10px;
        margin-top: 2px;
        margin-bottom: 10px;
    }

    &__availability {
        display: flex;
        gap: 4px;

        @include screen-md {
            gap: 10px;
        }

        img {
            width: 18px;

            @include screen-md {
                gap: 22px;
            }
        }
    }

    &__add-basket {
        position: absolute;
        bottom: 6px;
        right: 6px;
        border-radius: 15px;
    }

    &:hover {
        .product-card {
            &__favorite-button {
                display: flex;
            }

            &__title {
                color: $color-cta;
            }

            &__add-basket {
                display: inline-block;
            }
        }
    }
}
</style>
